import { zodResolver } from '@hookform/resolvers/zod';
import { DeleteOutline } from '@mui/icons-material';
import { Box, Button, FormHelperText, Grid, IconButton } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import {
  ControlledField,
  DataTable,
  DatePickerInputField,
  TimePickerInputField,
  colors,
  getDateLongFormatWithLocale,
} from 'remote/global';
import { useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PaginationQueryParams } from 'api/types';
import { runSequentially } from 'utils/helpers';
import { v4 as uuidv4 } from 'uuid';
import VehicleChangeLocationModal from 'components/modals/VehicleChangeLocationModal';
import {
  useDeleteOperatorsVehiclesTemporaryBaseMutation,
  usePostOperatorsVehiclesTemporaryBaseMutation,
  usePutOperatorsVehiclesTemporaryBaseMutation,
} from 'api/queries/operators';
import {
  VehicleBaseLocationSchemaType,
  VehicleLocationItemSchemaType,
  VehicleLocationListSchemaType,
  vehicleLocationListSchema,
} from './types';
import { compareFieldArrays } from '../helpers';

const emptyBaseObj: VehicleBaseLocationSchemaType = {
  id: 0, // id needs to be overwritten with a uuid string
  location: undefined as any, // This will make zod validation work and mark 'location' as required
  validFrom: '',
  validTo: '',
};

function createEmptyBaseObj(id: string) {
  return {
    ...emptyBaseObj,
    id,
  };
}

function VehicleLocationTableForm({
  defaultValues,
  vehicleId,
  setQueryParams,
  isQueryLoading,
}: {
  defaultValues?: VehicleLocationListSchemaType;
  vehicleId: number;
  setQueryParams: React.Dispatch<React.SetStateAction<PaginationQueryParams>>;
  isQueryLoading: boolean;
}) {
  const [selectedRow, setSelectedRow] = useState<VehicleBaseLocationSchemaType>();
  const [isChangeVehicleLocationOpen, setIsChangeVehicleLocationOpen] = useState(false);
  const tInstance = useTranslation();
  const { t, i18n } = tInstance;
  const postOperatorsVehiclesTemporaryBaseMutation = usePostOperatorsVehiclesTemporaryBaseMutation(
    String(vehicleId)
  );
  const putOperatorsVehiclesTemporaryBaseMutation = usePutOperatorsVehiclesTemporaryBaseMutation(
    String(vehicleId)
  );
  const deleteOperatorsVehiclesTemporaryBaseMutation =
    useDeleteOperatorsVehiclesTemporaryBaseMutation(String(vehicleId));

  const {
    handleSubmit,
    control,
    watch,
    clearErrors,
    getValues,
    formState: { isSubmitting, isValidating, isLoading, errors, isDirty },
  } = useForm<VehicleLocationListSchemaType>({
    defaultValues: {
      locations: undefined,
      ...defaultValues,
    },
    resolver: zodResolver(vehicleLocationListSchema),
  });
  const locations = watch('locations');
  const isPublishDisabled = !isDirty || isValidating || isSubmitting;

  const { fields, prepend, remove, update } = useFieldArray({
    control,
    name: 'locations',
    keyName: 'fieldPropertyId',
  });

  const onValid = async (data: VehicleLocationListSchemaType) => {
    const result = compareFieldArrays(defaultValues?.locations ?? [], data.locations);
    const { inserted, changed, removed } = result;

    const removedPromises = removed.map(
      (base) => () => deleteOperatorsVehiclesTemporaryBaseMutation.mutateAsync(String(base.id))
    );

    const insertedPromises = inserted.map((base) => () => {
      // We can't send the object with 'id' to BE, it gets errored
      const { id, ...rest } = base;
      return postOperatorsVehiclesTemporaryBaseMutation.mutateAsync(rest);
    });

    const changedPromises = changed.map(
      (base) => () =>
        putOperatorsVehiclesTemporaryBaseMutation.mutateAsync({ ...base, id: Number(base.id) })
    );

    // Run promises sequentially
    await runSequentially(removedPromises);
    await runSequentially(insertedPromises);
    await runSequentially(changedPromises);
  };

  const handleClickVehicleLocation = (row: VehicleBaseLocationSchemaType) => {
    setIsChangeVehicleLocationOpen(true);
    setSelectedRow(row);
  };

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'address',
        headerName: t('FleetView.DetailView.vehicleLocation.table.header.address') as string,
        flex: 2,
        minWidth: 300,
        sortable: false,
        renderCell: (data) => {
          const index = getValues('locations').findIndex((l) => l.id === data.id);
          if (index === -1) return null;
          const error = errors?.locations?.[index]?.location;
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Button
                sx={{
                  p: 0,
                  justifyContent: 'flex-start',
                  maxHeight: '18px',
                  color: error ? `${colors.busyRed}!important` : colors.uiBlue600,
                }}
                variant='text'
                disableElevation
                onClick={() => handleClickVehicleLocation(data.row)}
              >
                {data.row.location?.address ||
                  t('FleetView.DetailView.vehicleLocation.table.insertAddress')}
              </Button>
              <FormHelperText error={Boolean(error)}>{error?.message}</FormHelperText>
            </Box>
          );
        },
      },
      {
        field: 'startDate',
        headerName: t('FleetView.DetailView.vehicleLocation.table.header.startDate') as string,
        flex: 1,
        minWidth: 180,
        sortable: false,
        renderCell: (data) => {
          const index = getValues('locations').findIndex((l) => l.id === data.id);
          if (index === -1) return null;
          return (
            <ControlledField
              control={control}
              name={`locations.${index}.validFrom`}
              render={(renderProps) => (
                <DatePickerInputField
                  {...renderProps}
                  fieldProps={{
                    disablePast: true,
                    format: getDateLongFormatWithLocale(i18n.language),
                  }}
                />
              )}
            />
          );
        },
      },
      {
        field: 'startTime',
        headerName: t('FleetView.DetailView.vehicleLocation.table.header.startTime') as string,
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (data) => {
          const index = getValues('locations').findIndex((l) => l.id === data.id);
          if (index === -1) return null;
          return (
            <ControlledField
              control={control}
              name={`locations.${index}.validFrom`}
              render={(renderProps) => <TimePickerInputField {...renderProps} />}
            />
          );
        },
      },
      {
        field: 'endDate',
        headerName: t('FleetView.DetailView.vehicleLocation.table.header.endDate') as string,
        flex: 1,
        minWidth: 180,
        sortable: false,
        renderCell: (data) => {
          const index = getValues('locations').findIndex((l) => l.id === data.id);
          if (index === -1) return null;
          return (
            <ControlledField
              control={control}
              name={`locations.${index}.validTo`}
              render={(renderProps) => (
                <DatePickerInputField
                  {...renderProps}
                  fieldProps={{
                    disablePast: true,
                    format: getDateLongFormatWithLocale(i18n.language),
                  }}
                />
              )}
            />
          );
        },
      },
      {
        field: 'endTime',
        headerName: t('FleetView.DetailView.vehicleLocation.table.header.endTime') as string,
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (data) => {
          const index = getValues('locations').findIndex((l) => l.id === data.id);
          if (index === -1) return null;
          return (
            <ControlledField
              control={control}
              name={`locations.${index}.validTo`}
              render={(renderProps) => <TimePickerInputField {...renderProps} />}
            />
          );
        },
      },
      {
        field: 'modify',
        headerName: ' ',
        flex: 1,
        minWidth: 90,
        sortable: false,
        renderCell: ({ row }) => (
          <Box
            sx={{
              display: 'flex',
              textAlign: 'right',
              width: '100%',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <IconButton
              size='large'
              sx={{
                padding: 0,
                color: colors.gray2,
              }}
              onClick={async () => {
                const index = fields.findIndex((i) => i.fieldPropertyId === row.fieldPropertyId);
                if (index !== -1) remove(index);
              }}
            >
              <DeleteOutline color='primary' />
            </IconButton>
          </Box>
        ),
      },
    ];
  }, [t, remove, control, i18n.language, fields, getValues, errors]);

  const handleClearChangeVehicleLocationModalState = () => {
    setIsChangeVehicleLocationOpen(false);
    setSelectedRow(undefined);
  };

  const handleUpdateRowLocation = async (location: VehicleLocationItemSchemaType) => {
    if (selectedRow == null || location == null) return;

    // Get the index of the selected row
    const rowIndex = locations.findIndex((i) => i.id === selectedRow.id);
    if (rowIndex === -1) return;

    const field = locations[rowIndex];
    update(rowIndex, {
      ...field,
      location: {
        ...field.location,
        ...location,
      },
    });

    // At this point we can also reset the 'location' error as we can be certain it has been resolved
    clearErrors(`locations.${rowIndex}.location`);

    // Closes the modal and resets state
    handleClearChangeVehicleLocationModalState();
  };

  return (
    <>
      <VehicleChangeLocationModal
        open={isChangeVehicleLocationOpen}
        setOpen={() => setIsChangeVehicleLocationOpen(false)}
        onClose={handleClearChangeVehicleLocationModalState}
        defaultValues={selectedRow!}
        onConfirm={handleUpdateRowLocation}
      />
      <form onSubmit={handleSubmit(onValid)} style={{ width: '100%' }}>
        <Grid container gap={2}>
          <Grid item xs={12} display='flex' justifyContent='space-between'>
            <Button onClick={() => prepend(createEmptyBaseObj(uuidv4()))}>
              {t('FleetView.DetailView.vehicleLocation.actions.addRow')}
            </Button>
            <Button type='submit' variant='contained' disabled={isPublishDisabled || isLoading}>
              {t('publish')}
            </Button>
          </Grid>
          <Grid item xs={12}>
            <DataTable
              rows={{ elements: fields }}
              columns={columns}
              loading={isQueryLoading}
              getRowId={(row) => row.id}
              onPageChange={(page) => setQueryParams((p) => ({ ...p, pageNo: String(page) }))}
              t={t}
            />
          </Grid>
        </Grid>
      </form>
    </>
  );
}

export default VehicleLocationTableForm;
